/**
 * Public read-only share view for a proposal — no auth required.
 * Renders the same content as the PDF in clean HTML so the client can
 * read it on any device. Includes interactive item / domain selection,
 * live total recompute and a drawn-signature acceptance block.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Loader2, CheckCircle2, FileText, ExternalLink, Lock, Eraser,
  PenLine, ShieldCheck, Printer,
} from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const absUrl = (u) => (u && u.startsWith("/") ? `${BACKEND}${u}` : u);

function fmtMoney(v, cur = "AUD") {
  const sym = { AUD: "$", USD: "$", GBP: "£", EUR: "€", NZD: "$" }[cur] || "";
  return `${sym}${Math.round(Number(v || 0)).toLocaleString()}`;
}

// Split "Title — Client Name" so the share view + PDF render the client
// name on its own subtitle line under the proposal title.
function splitTitle(t = "") {
  const m = t.match(/^(.+?)\s*[—–-]\s*(.+)$/);
  if (m) return { main: m[1].trim(), sub: m[2].trim() };
  return { main: t, sub: "" };
}

// ── Selection helpers ───────────────────────────────────────────────────
// Seed the initial selection map: required items + domains are ON,
// optional items + domains are OFF. Inside each radio group, the first
// required item wins; if none required, the first item is preselected.
// Keys mirror the render fallback (`it.id || ${sec.key}-{kind}-{idx}`) so
// the seeded map matches the ids used by the <input>s.
function _itemKey(sec, it, kind, idx, gKey) {
  if (it.id) return it.id;
  return kind === "group"
    ? `${sec.key}-${gKey}-${idx}`
    : `${sec.key}-std-${idx}`;
}

function seedSelections(doc) {
  const items = {};
  const domains = {};

  (doc.sections || []).forEach((sec) => {
    // Mirror the PDF: show every item with qty>0 (the "menu"). Initial
    // tick state == `it.included` so the URL preload exactly matches
    // what the printable PDF reflects in the Investment summary.
    const visible = (sec.items || []).filter(
      (it) => !it.hidden && (it.qty || 0) > 0,
    );

    // requires_selection turns the whole section into one radio group,
    // overriding any per-item choice_group within. Winner priority:
    // recommended → first included → first item — matches backend
    // `_effective_items` exactly so URL preload ↔ totals ↔ PDF agree.
    if (sec.requires_selection && visible.length > 0) {
      const recIdx = visible.findIndex((x) => x.recommended);
      const incIdx = visible.findIndex((x) => x.included);
      const winnerIdx = recIdx >= 0 ? recIdx : (incIdx >= 0 ? incIdx : 0);
      visible.forEach((it, vi) => {
        items[_itemKey(sec, it, "std", vi)] = (vi === winnerIdx);
      });
      return;
    }

    const standalone = [];
    const groupOrder = [];
    const grouped = {};
    visible.forEach((it) => {
      if (it.choice_group) {
        if (!grouped[it.choice_group]) {
          grouped[it.choice_group] = [];
          groupOrder.push(it.choice_group);
        }
        grouped[it.choice_group].push(it);
      } else {
        standalone.push(it);
      }
    });
    // Standalone (checkbox) items — pre-tick if currently `included`
    // OR if explicitly flagged `recommended`. Recommended is the
    // unlocked equivalent of required: pre-selected on first load, but
    // the client can still un-tick it.
    standalone.forEach((it, sIdx) => {
      items[_itemKey(sec, it, "std", sIdx)] = !!it.included || !!it.recommended;
    });
    // Choice groups (radio) — only one can win. Same priority as
    // requires_selection: recommended → first included → first.
    groupOrder.forEach((g) => {
      const arr = grouped[g];
      const recIdx = arr.findIndex((x) => x.recommended);
      const incIdx = arr.findIndex((x) => x.included);
      const winnerIdx = recIdx >= 0 ? recIdx : (incIdx >= 0 ? incIdx : 0);
      arr.forEach((it, ai) => {
        items[_itemKey(sec, it, "group", ai, g)] = (ai === winnerIdx);
      });
    });
  });

  (doc.domain_options || []).forEach((d, i) => {
    domains[i] = !!d.required || !!d.recommended;
  });

  return { items, domains };
}

const RECURRING_LABEL = {
  one_off: "One-off", monthly: "Monthly", quarterly: "Quarterly",
  biennial: "Biennial", annual: "Annual",
};
const cadenceLabel = (r) => RECURRING_LABEL[r] || "One-off";

// Live totals — mirrors backend compute_totals using only the items the
// client has currently selected. Domain renewals are bucketed by
// renewal_years (2 → biennial, otherwise → annual).
function computeLiveTotals(doc, sel, mode) {
  const gst = Number(doc.gst_rate || 0);
  // Admin preview always uses the live `sel` (so they can see what
  // ticking different options would do). Real public URL on an
  // accepted proposal locks to the historical signed selections.
  const locked = mode !== "preview" && doc.status === "accepted";
  let one_off_subtotal = 0, monthly_subtotal = 0, quarterly_subtotal = 0,
      biennial_subtotal = 0, annual_subtotal = 0;
  (doc.sections || []).forEach((sec) => {
    const visible = (sec.items || []).filter(
      (it) => !it.hidden && (it.qty || 0) > 0,
    );
    const tally = (it) => {
      const line = Number(it.qty || 0) * Number(it.unit_price || 0);
      if (it.recurring === "monthly") monthly_subtotal += line;
      else if (it.recurring === "quarterly") quarterly_subtotal += line;
      else if (it.recurring === "biennial") biennial_subtotal += line;
      else if (it.recurring === "annual") annual_subtotal += line;
      else one_off_subtotal += line;
    };

    // requires_selection: only one item is ever picked — keep keys in
    // sync with seedSelections / render path.
    if (sec.requires_selection && visible.length > 0) {
      visible.forEach((it, vi) => {
        if (sel.items[_itemKey(sec, it, "std", vi)]) tally(it);
      });
      return;
    }

    const standalone = [];
    const groupOrder = [];
    const grouped = {};
    visible.forEach((it) => {
      if (it.choice_group) {
        if (!grouped[it.choice_group]) {
          grouped[it.choice_group] = [];
          groupOrder.push(it.choice_group);
        }
        grouped[it.choice_group].push(it);
      } else {
        standalone.push(it);
      }
    });
    standalone.forEach((it, sIdx) => {
      if (sel.items[_itemKey(sec, it, "std", sIdx)]) tally(it);
    });
    groupOrder.forEach((g) => {
      grouped[g].forEach((it, ai) => {
        if (sel.items[_itemKey(sec, it, "group", ai, g)]) tally(it);
      });
    });
  });
  // Domain renewals. After accept the canonical signal is
  // `accepted_selections.domains`; pre-accept we read live `sel.domains`.
  const domainPicks = locked
    ? (doc.accepted_selections?.domains || {})
    : (sel.domains || {});
  (doc.domain_options || []).forEach((d, i) => {
    if (!domainPicks[i]) return;
    const price = Number(d.renewal_price || 0);
    if (price <= 0) return;
    if (Number(d.renewal_years || 1) === 2) biennial_subtotal += price;
    else annual_subtotal += price;
  });
  const one_off_total = one_off_subtotal * (1 + gst);
  const monthly_total = monthly_subtotal * (1 + gst);
  const quarterly_total = quarterly_subtotal * (1 + gst);
  const biennial_total = biennial_subtotal * (1 + gst);
  const annual_total = annual_subtotal * (1 + gst);
  // Year-one cash outflow (ex-GST): monthly × 12 + quarterly × 4 +
  // biennial × 1 (full charge paid year one — billed every 24 months)
  // + annual × 1 + one-offs.
  const year_one_subtotal = one_off_subtotal + monthly_subtotal * 12
    + quarterly_subtotal * 4 + biennial_subtotal + annual_subtotal;
  const year_one_total = year_one_subtotal * (1 + gst);
  return {
    one_off_subtotal, monthly_subtotal, quarterly_subtotal,
    biennial_subtotal, annual_subtotal,
    one_off_total, monthly_total, quarterly_total,
    biennial_total, annual_total,
    year_one_subtotal, year_one_total, gst_rate: gst,
  };
}

/**
 * Renders a proposal in client-facing form. Two modes:
 *   - public share view (route `/proposals/share/:token`, no auth)
 *   - admin preview (route `/clients/:clientId/proposals/:proposalId/view`,
 *     auth required) — lets the sales manager see exactly what the client
 *     will see, including for draft proposals that don't yet have a
 *     share_token.
 */
export default function ProposalShareView({ mode = "share" }) {
  const params = useParams();
  const token = params.token;
  const proposalId = params.proposalId;
  const [doc, setDoc] = useState(null);
  const [err, setErr] = useState(null);

  // Client-side selections — keyed by item id / domain index.
  const [sel, setSel] = useState({ items: {}, domains: {} });
  // Client-owned domain(s) the client types in on the share view —
  // autosaved (debounced) via /client-owned-domains, then frozen as
  // part of the acceptance audit trail.
  const [ownedDomains, setOwnedDomains] = useState([""]);
  const [ownedDomainsStatus, setOwnedDomainsStatus] = useState("idle"); // idle | saving | saved | error
  const ownedDomainsTimer = useRef(null);
  // Signature pad state
  const sigCanvasRef = useRef(null);
  const [signedName, setSignedName] = useState("");
  const [signedTitle, setSignedTitle] = useState("");
  const [accepting, setAccepting] = useState(false);
  // Track whether the client has drawn ANY stroke on the canvas — a
  // pristine canvas is treated as "no signature" for validation.
  const [sigDirty, setSigDirty] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        if (mode === "preview" && proposalId) {
          // Admin preview — authenticated, doesn't flip status to 'viewed'.
          const { data } = await api.get(`/proposals/${proposalId}/preview`);
          setDoc(data);
          setSel(seedSelections(data));
          const existing = (data.client_owned_domains || []).filter(Boolean);
          setOwnedDomains(existing.length ? existing : [""]);
        } else {
          // Public share — uses raw axios since the share endpoint is
          // unauthenticated (no JWT header needed).
          const { data } = await axios.get(`${BACKEND}/api/proposals/share/${token}`);
          setDoc(data);
          setSel(seedSelections(data));
          const existing = (data.client_owned_domains || []).filter(Boolean);
          setOwnedDomains(existing.length ? existing : [""]);
        }
      } catch (e) {
        setErr(e.response?.data?.detail || "Proposal not found");
      }
    })();
  }, [mode, token, proposalId]);

  // Re-derive totals whenever selections change.
  const liveTotals = useMemo(
    () => (doc ? computeLiveTotals(doc, sel, mode) : {}),
    [doc, sel, mode],
  );

  if (err) {
    return (
      <div className="min-h-screen grid place-items-center bg-zinc-50 px-6">
        <div className="text-center space-y-2">
          <div className="font-display text-2xl">Proposal not found</div>
          <div className="text-sm text-zinc-500">{err}</div>
        </div>
      </div>
    );
  }
  if (!doc) {
    return (
      <div className="min-h-screen grid place-items-center bg-zinc-50">
        <Loader2 className="h-6 w-6 animate-spin text-zinc-400" />
      </div>
    );
  }

  const totals = doc.status === "accepted"
    ? (doc.accepted_totals && Object.keys(doc.accepted_totals).length
        ? doc.accepted_totals : (doc.totals || {}))
    : liveTotals;
  const cur = doc.currency || "AUD";
  // 'locked' = the proposal is irreversibly accepted AND we're on the
  // public-facing URL → render the read-only signed summary. Admin
  // preview mode unlocks so the admin can verify how seedSelections
  // would render the proposal as if it were freshly opened (e.g. to
  // see whether the recommended hosting tier is correctly winning).
  const locked = mode !== "preview" && doc.status === "accepted";
  const signingDisabled = mode === "preview" || locked;

  const toggleItem = (id, v) => {
    if (locked) return;
    setSel((s) => ({ ...s, items: { ...s.items, [id]: !!v } }));
  };
  const pickRadio = (id, groupIds) => {
    if (locked) return;
    setSel((s) => {
      const next = { ...s.items };
      groupIds.forEach((gid) => { next[gid] = gid === id; });
      return { ...s, items: next };
    });
  };
  const toggleDomain = (i, v) => {
    if (locked) return;
    setSel((s) => ({ ...s, domains: { ...s.domains, [i]: !!v } }));
  };
  const allDomains = (v) => {
    if (locked) return;
    setSel((s) => {
      const next = { ...s.domains };
      (doc.domain_options || []).forEach((d, i) => {
        // Required domains stay ticked regardless of "clear all".
        next[i] = v ? true : !!d.required;
      });
      return { ...s, domains: next };
    });
  };

  // ── Client-owned domains: debounced autosave ───────────────────────
  // Persists the typed values to the backend ~600 ms after the last
  // keystroke so a refresh / device-switch keeps what the client has
  // typed. No-op in admin preview mode.
  const scheduleOwnedDomainsSave = (next) => {
    if (mode === "preview" || !token || locked) return;
    setOwnedDomainsStatus("saving");
    if (ownedDomainsTimer.current) clearTimeout(ownedDomainsTimer.current);
    ownedDomainsTimer.current = setTimeout(async () => {
      try {
        await axios.patch(
          `${BACKEND}/api/proposals/share/${token}/client-owned-domains`,
          { domains: (next || []).map((d) => (d || "").trim()).filter(Boolean) },
        );
        setOwnedDomainsStatus("saved");
      } catch (e) {
        setOwnedDomainsStatus("error");
      }
    }, 600);
  };

  const updateOwnedDomain = (idx, value) => {
    setOwnedDomains((prev) => {
      const next = [...(prev || [])];
      next[idx] = value;
      scheduleOwnedDomainsSave(next);
      return next;
    });
  };

  const addOwnedDomain = () => {
    setOwnedDomains((prev) => {
      const next = [...(prev || []), ""];
      return next.length > 20 ? prev : next;
    });
  };

  const removeOwnedDomain = (idx) => {
    setOwnedDomains((prev) => {
      const arr = (prev || []).filter((_, i) => i !== idx);
      const next = arr.length === 0 ? [""] : arr;
      scheduleOwnedDomainsSave(next);
      return next;
    });
  };

  const accept = async () => {
    if (mode === "preview") {
      toast.info("This is the preview — open the client share link to actually accept.");
      return;
    }
    if (!signedName.trim()) {
      toast.error("Please type your full legal name to sign.");
      return;
    }
    if (!signedTitle.trim()) {
      toast.error("Please type your position / title in the company.");
      return;
    }
    if (!sigDirty || !sigCanvasRef.current) {
      toast.error("Please draw your signature in the box above.");
      return;
    }
    const dataUrl = sigCanvasRef.current.toDataURL("image/png");
    setAccepting(true);
    try {
      await axios.post(
        `${BACKEND}/api/proposals/share/${token}/accept`,
        {
          signed_name: signedName.trim(),
          signed_title: signedTitle.trim(),
          signature_png: dataUrl,
          selections: sel,
          totals,
          // Stamp the final list of client-owned domains as part of the
          // signed audit trail (autosave may have lagged on the last
          // keystroke).
          client_owned_domains: (ownedDomains || [])
            .map((d) => (d || "").trim()).filter(Boolean),
        },
      );
      toast.success("Proposal accepted — thank you!");
      // Re-fetch so the locked view renders.
      const { data } = await axios.get(`${BACKEND}/api/proposals/share/${token}`);
      setDoc(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Acceptance failed");
    } finally {
      setAccepting(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-50 py-10 px-6" data-testid="proposal-share-view">
      {/* Print-only styles — scoped to this page. Keeps each section
          (and its child item rows / cards / signature block) on one
          page where possible, hides the preview banner + print button
          itself, and removes background colours that look muddy in
          ink. Uses `break-inside: avoid` (the modern alternative to
          page-break-inside) with the legacy fallback. */}
      <style>{`
        @media print {
          @page { margin: 14mm; }
          html, body { background: white !important; }
          [data-testid="proposal-preview-banner"],
          [data-testid="proposal-share-print"],
          [data-testid="proposal-share-print-wrap"] { display: none !important; }
          [data-testid="proposal-share-view"] { padding: 0 !important; background: white !important; }
          .proposal-shell { box-shadow: none !important; border: 0 !important; }
          /* Page break rules — keep cover on page 1, brief on page 2,
              domain options on page 3, then sections flow naturally. */
          [data-testid="proposal-share-brief"],
          [data-testid="proposal-share-domain-options"] {
            page-break-before: always;
            break-before: page;
          }
          /* Keep section blocks together across page breaks. */
          [data-testid^="proposal-share-section-"],
          [data-testid="proposal-share-signature-block"],
          [data-testid="proposal-share-totals"],
          [data-testid="proposal-share-acceptance"],
          [data-testid="proposal-share-brief"],
          [data-testid="proposal-share-domain-options"] {
            page-break-inside: avoid;
            break-inside: avoid;
            border-bottom: 1px solid #ddd;
          }
          /* Individual item rows + radio cards shouldn't split either. */
          [data-testid^="proposal-share-item-"] {
            page-break-inside: avoid;
            break-inside: avoid;
          }
          /* Force the dark totals strip to print with its colour. */
          [data-testid="proposal-share-totals"] {
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
        }
      `}</style>
      {mode === "preview" && (
        <div className="max-w-3xl mx-auto mb-3 px-3 py-2 rounded-sm bg-amber-50 border border-amber-200 text-[12px] text-amber-900 flex items-center gap-2"
          data-testid="proposal-preview-banner">
          <span className="font-mono uppercase tracking-wider text-[10px] px-1.5 py-0.5 rounded-sm bg-amber-200 text-amber-900">Preview</span>
          You&apos;re viewing this proposal as the client would see it. Status hasn&apos;t changed.
        </div>
      )}
      <div className="max-w-3xl mx-auto bg-white rounded-sm shadow-sm border border-zinc-200">
        {/* Header */}
        <div className="p-10 border-b border-zinc-100 relative">
          {/* Print button — opens the browser's print dialog. The
              print stylesheet above keeps each section together so
              related rows don't break across pages. */}
          <div className="absolute top-6 right-6"
            data-testid="proposal-share-print-wrap">
            <button type="button" onClick={() => window.print()}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-sm text-white text-sm shadow-sm hover:opacity-90 transition-opacity"
              style={{ background: doc.from_color_primary || "#002FA7" }}
              data-testid="proposal-share-print">
              <Printer className="h-4 w-4" /> Print
            </button>
          </div>
          {doc.from_logo_url && (
            <img src={absUrl(doc.from_logo_url)} alt="" className="h-16 object-contain mb-4"
              data-testid="proposal-share-logo" />
          )}
          {/* Overline removed — title alone is enough context. */}
          {(() => {
            const { main, sub } = splitTitle(doc.title || "");
            return (
              <>
                <h1 className="font-display font-extrabold text-4xl tracking-tight mt-2"
                  data-testid="proposal-share-title">
                  {main || "Untitled proposal"}
                </h1>
                {sub && (
                  <div className="font-display font-bold text-2xl text-zinc-700 mt-1"
                    data-testid="proposal-share-subtitle">
                    {sub}
                  </div>
                )}
              </>
            );
          })()}
          <div className="text-sm text-zinc-600 mt-4 space-y-0.5">
            {doc.prepared_for && <div><b>Prepared for:</b> {doc.prepared_for}</div>}
            <div><b>Date:</b> {new Date().toLocaleDateString("en-AU", { day: "2-digit", month: "long", year: "numeric" })}</div>
            {doc.valid_until && <div><b>Valid until:</b> {doc.valid_until.slice(0, 10)}</div>}
            {/* Prepared by + sender details — grouped together so the
                client reads "who's sending this" as one identity block
                (Prepared by line + address + ABN + phone + email + web). */}
            {(doc.prepared_by || doc.from_address || doc.from_phone || doc.from_email || doc.from_website || doc.from_abn) && (
              <ul className="text-[12px] text-zinc-500 pt-2 border-t border-zinc-100 mt-3 space-y-0.5 list-none"
                data-testid="proposal-share-sender-details">
                {doc.prepared_by && (
                  <li className="text-sm text-zinc-600">
                    <b>Prepared by:</b> {doc.prepared_by}
                  </li>
                )}
                {[
                  doc.from_address,
                  doc.from_abn && `ABN ${doc.from_abn}`,
                  doc.from_phone,
                  doc.from_email,
                  doc.from_website,
                ].filter(Boolean).map((line) => (
                  <li key={line}>{line}</li>
                ))}
              </ul>
            )}
          </div>
          <div className="mt-6 flex items-center gap-3">
            {doc.status === "viewed" && (
              <span className="inline-flex items-center gap-1 text-[11px] font-mono text-emerald-700">
                <CheckCircle2 className="h-3 w-3" /> opened
              </span>
            )}
          </div>
        </div>

        {/* Objectives / intro */}
        {(doc.introduction || doc.objectives) && (
          <div className="p-10 border-b border-zinc-100 space-y-6"
            data-testid="proposal-share-brief">
            {doc.objectives && (
              <Section title="Project objectives (client brief)" body={doc.objectives} />
            )}
            {doc.introduction && (
              <Section title="Proposal overview" body={doc.introduction} />
            )}
          </div>
        )}

        {/* Domain options */}
        {(doc.domain_options || []).length > 0 && (
          <div className="p-10 border-b border-zinc-100"
            data-testid="proposal-share-domain-options">
            <div className="flex items-end justify-between gap-3 flex-wrap mb-4">
              <h2 className="font-display font-bold text-2xl">Domain name options</h2>
              {!locked && (
                <div className="flex items-center gap-2" data-testid="proposal-share-domain-bulk">
                  <button type="button"
                    onClick={() => allDomains(true)}
                    className="text-[11px] px-2 py-1 rounded-sm border border-zinc-200 hover:bg-zinc-50"
                    data-testid="proposal-share-domain-select-all">
                    Select all
                  </button>
                  <button type="button"
                    onClick={() => allDomains(false)}
                    className="text-[11px] px-2 py-1 rounded-sm border border-zinc-200 hover:bg-zinc-50"
                    data-testid="proposal-share-domain-clear-all">
                    Clear all
                  </button>
                </div>
              )}
            </div>
            <div className="grid gap-2">
              {doc.domain_options.map((d, i) => {
                const isPicked = locked
                  ? !!(doc.accepted_selections?.domains?.[i])
                  : !!sel.domains[i];
                const isRequired = !!d.required;
                return (
                  <label key={d.domain || `__d_${i}`}
                    className={`grid grid-cols-12 gap-2 items-center p-3 border rounded-sm cursor-pointer ${
                      isPicked ? "border-zinc-900 bg-zinc-50" : "border-zinc-100"
                    } ${isRequired ? "ring-1 ring-amber-200" : ""}`}
                    data-testid={`proposal-share-domain-${i}`}>
                    <div className="col-span-1 flex items-center">
                      <input type="checkbox" checked={isPicked}
                        disabled={isRequired || locked}
                        onChange={(e) => toggleDomain(i, e.target.checked)}
                        data-testid={`proposal-share-domain-${i}-checkbox`} />
                      {isRequired && <Lock className="h-3 w-3 text-amber-600 ml-1.5" />}
                    </div>
                    <div className="col-span-4 font-mono text-sm">
                      {d.preferred && <span className="text-amber-600 mr-1">★</span>}
                      {d.domain}
                    </div>
                    <div className="col-span-2 text-[10px] uppercase font-mono">{
                      // Surface 'manual_available' (admin override saying "client owns
                      // this") as the same simple 'Available' label so the client view
                      // doesn't leak our internal vocabulary.
                      (() => {
                        const a = (d.availability || "unknown").toLowerCase();
                        return a === "manual_available" ? "AVAILABLE" : a.toUpperCase();
                      })()
                    }</div>
                    <div className="col-span-2 font-mono text-xs text-right">
                      {Number(d.renewal_price || 0) > 0 && (
                        <>{fmtMoney(d.renewal_price, cur)} <span className="text-zinc-500">/ {d.renewal_years || 1}yr{(d.renewal_years || 1) === 1 ? "" : "s"}</span></>
                      )}
                    </div>
                    <div className="col-span-3 text-[11px] text-zinc-500">{d.note}</div>
                  </label>
                );
              })}
            </div>
            <p className="text-[11px] text-zinc-500 mt-3">
              Domain registration is paid monthly in advance — billed across the renewal term shown above.
              {!locked && (
                <> Required domains are locked and pre-selected.</>
              )}
            </p>
            <ClientOwnedDomainsBlock
              ownedDomains={ownedDomains}
              status={ownedDomainsStatus}
              locked={locked}
              onUpdate={updateOwnedDomain}
              onAdd={addOwnedDomain}
              onRemove={removeOwnedDomain}
              isAccepted={doc.status === "accepted"}
            />
          </div>
        )}

        {/* Client-owned domains — standalone block when no agency
            domain_options are present, so the client can still tell us
            what domain they already own. */}
        {(doc.domain_options || []).length === 0 && (
          <div className="p-10 border-b border-zinc-100"
            data-testid="proposal-share-domain-options">
            <h2 className="font-display font-bold text-2xl mb-4">Domain name options</h2>
            <ClientOwnedDomainsBlock
              ownedDomains={ownedDomains}
              status={ownedDomainsStatus}
              locked={locked}
              onUpdate={updateOwnedDomain}
              onAdd={addOwnedDomain}
              onRemove={removeOwnedDomain}
              isAccepted={doc.status === "accepted"}
              standalone
            />
          </div>
        )}

        {/* Sections */}
        {(doc.sections || []).map((sec) => {
          const visible = (sec.items || []).filter(
            (it) => !it.hidden && it.qty > 0,
          );
          // Hide the whole section unless at least one item is in the
          // deal (included || recommended). Mirrors the PDF's
          // `has_items` rule so the document and the URL agree.
          const anySelected = visible.some(
            (it) => it.included || it.recommended,
          );
          // Info-only sections (no items, but a description block) render
          // too — used by SEO basics / AIO basics explainer blocks.
          const infoOnly = visible.length === 0 && !!(sec.description || "").trim() && (sec.items || []).length === 0;
          if (visible.length === 0 && !infoOnly) return null;
          if (visible.length > 0 && !anySelected) return null;

          // requires_selection: render the whole section as a single
          // radio group, overriding any per-item choice_group within.
          const sectionRadio = !!sec.requires_selection && visible.length > 0;

          // Bucket items into a) standalone (each rendered as a checkbox)
          //                  b) choice-groups (rendered as a radio block)
          // (Bypassed entirely when sectionRadio is true.)
          const grouped = {};
          const standalone = [];
          const groupOrder = [];
          if (!sectionRadio) {
            visible.forEach((it) => {
              if (it.choice_group) {
                if (!grouped[it.choice_group]) {
                  grouped[it.choice_group] = [];
                  groupOrder.push(it.choice_group);
                }
                grouped[it.choice_group].push(it);
              } else {
                standalone.push(it);
              }
            });
          }

          return (
            <div key={sec.key} className="p-10 border-b border-zinc-100"
              data-testid={`proposal-share-section-${sec.key}`}>
              <div className="flex items-center gap-2 flex-wrap mb-2">
                <h2 className="font-display font-bold text-2xl">{sec.title}</h2>
                {sectionRadio && (
                  <span className="inline-flex items-center text-[10px] font-mono uppercase tracking-wide px-2 py-0.5 rounded-sm bg-amber-100 text-amber-800"
                    data-testid={`proposal-share-section-${sec.key}-choose-one`}>
                    Choose one · required
                  </span>
                )}
              </div>
              {sec.description && (
                <div className="text-sm text-zinc-700 leading-relaxed mb-4 whitespace-pre-line">
                  {sec.description}
                </div>
              )}

              {/* Reference links — sample websites, example posts. Always
                  open in a new tab so the client doesn't lose their place
                  on the proposal. (Definition moved to bottom of section
                  so it appears as a clear call-to-action below the items
                  table — see render below.) */}

              {/* Section-level required choice — all visible items
                  render as a single radio group, locked to the seeded
                  recommended item by default. */}
              {sectionRadio && (
                <div className="space-y-2 mb-4"
                  data-testid={`proposal-share-section-${sec.key}-radio-block`}>
                  {(() => {
                    const ids = visible.map((it, ai) => _itemKey(sec, it, "std", ai));
                    return visible.map((it, ai) => {
                      const key = ids[ai];
                      const checked = locked
                        ? !!(doc.accepted_selections?.items?.[key])
                        : !!sel.items[key];
                      const line = (it.qty || 0) * (it.unit_price || 0);
                      return (
                        <label key={key}
                          className={`grid grid-cols-12 gap-3 items-start p-3 border rounded-sm cursor-pointer ${
                            checked ? "border-zinc-900 bg-zinc-50" : "border-zinc-100"
                          }`}
                          data-testid={`proposal-share-item-${key}`}>
                          <div className="col-span-1 pt-1">
                            <input type="radio" name={`section-${sec.key}`} checked={checked}
                              disabled={locked}
                              onChange={() => pickRadio(key, ids)}
                              data-testid={`proposal-share-item-${key}-radio`} />
                          </div>
                          <div className="col-span-7">
                            <div className="font-semibold text-sm">{it.title}</div>
                            {it.description && (
                              <div className="text-[12px] text-zinc-500 mt-0.5 whitespace-pre-line">
                                {it.description}
                              </div>
                            )}
                            {it.notes && (
                              <div className="text-[11px] italic text-zinc-500 mt-0.5 whitespace-pre-line">
                                {it.notes}
                              </div>
                            )}
                          </div>
                          <div className="col-span-2 font-mono text-xs text-right pt-1">
                            {it.qty} × {fmtMoney(it.unit_price, cur)}
                          </div>
                          <div className="col-span-2 text-right pt-1">
                            <div className="font-mono text-sm">{fmtMoney(line, cur)}</div>
                            <div className="text-[10px] uppercase font-mono text-zinc-500">
                              {cadenceLabel(it.recurring)}
                            </div>
                          </div>
                        </label>
                      );
                    });
                  })()}
                </div>
              )}

              {/* Standalone items as checkboxes */}
              {standalone.length > 0 && (
                <div className="space-y-2 mb-4">
                  {standalone.map((it, sIdx) => {
                    const key = it.id || `${sec.key}-std-${sIdx}`;
                    const checked = locked
                      ? !!(doc.accepted_selections?.items?.[key])
                      : !!sel.items[key];
                    const req = !!it.required;
                    const line = (it.qty || 0) * (it.unit_price || 0);
                    return (
                      <label key={key}
                        className={`grid grid-cols-12 gap-3 items-start p-3 border rounded-sm cursor-pointer ${
                          checked ? "border-zinc-900 bg-zinc-50" : "border-zinc-100"
                        } ${req ? "ring-1 ring-amber-200" : ""}`}
                        data-testid={`proposal-share-item-${key}`}>
                        <div className="col-span-1 pt-1 flex items-center gap-1">
                          <input type="checkbox" checked={checked}
                            disabled={req || locked}
                            onChange={(e) => toggleItem(key, e.target.checked)}
                            data-testid={`proposal-share-item-${key}-checkbox`} />
                          {req && <Lock className="h-3 w-3 text-amber-600" />}
                        </div>
                        <div className="col-span-7">
                          <div className="font-semibold text-sm">{it.title}</div>
                          {it.description && (
                            <div className="text-[12px] text-zinc-500 mt-0.5 whitespace-pre-line">
                              {it.description}
                            </div>
                          )}
                          {it.notes && (
                            <div className="text-[11px] italic text-zinc-500 mt-0.5 whitespace-pre-line">
                              {it.notes}
                            </div>
                          )}
                        </div>
                        <div className="col-span-2 font-mono text-xs text-right pt-1">
                          {it.qty} × {fmtMoney(it.unit_price, cur)}
                        </div>
                        <div className="col-span-2 text-right pt-1">
                          <div className="font-mono text-sm">{fmtMoney(line, cur)}</div>
                          <div className="text-[10px] uppercase font-mono text-zinc-500">
                            {cadenceLabel(it.recurring)}
                          </div>
                        </div>
                      </label>
                    );
                  })}
                </div>
              )}

              {/* Choice groups (radio blocks) */}
              {groupOrder.map((g) => {
                const arr = grouped[g];
                const ids = arr.map((x) => x.id);
                const reqId = arr.find((x) => x.required)?.id;
                return (
                  <div key={g}
                    className="border border-zinc-200 rounded-sm p-3 mb-4 bg-zinc-50/40"
                    data-testid={`proposal-share-group-${g}`}>
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2 flex items-center gap-1.5">
                      <PenLine className="h-3 w-3" /> Choose one option
                      {reqId && <span className="text-amber-700 normal-case tracking-normal">· locked to required</span>}
                    </div>
                    {arr.map((it) => {
                      const checked = locked
                        ? !!(doc.accepted_selections?.items?.[it.id])
                        : !!sel.items[it.id];
                      const line = (it.qty || 0) * (it.unit_price || 0);
                      const disabled = locked || !!reqId;
                      return (
                        <label key={it.id}
                          className={`grid grid-cols-12 gap-3 items-start p-3 border rounded-sm cursor-pointer mb-1 ${
                            checked ? "border-zinc-900 bg-white" : "border-zinc-100 bg-white"
                          }`}
                          data-testid={`proposal-share-item-${it.id}`}>
                          <div className="col-span-1 pt-1">
                            <input type="radio" name={`group-${g}`} checked={checked}
                              disabled={disabled}
                              onChange={() => pickRadio(it.id, ids)}
                              data-testid={`proposal-share-item-${it.id}-radio`} />
                          </div>
                          <div className="col-span-7">
                            <div className="font-semibold text-sm">{it.title}</div>
                            {it.description && (
                              <div className="text-[12px] text-zinc-500 mt-0.5 whitespace-pre-line">
                                {it.description}
                              </div>
                            )}
                          </div>
                          <div className="col-span-2 font-mono text-xs text-right pt-1">
                            {it.qty} × {fmtMoney(it.unit_price, cur)}
                          </div>
                          <div className="col-span-2 text-right pt-1">
                            <div className="font-mono text-sm">{fmtMoney(line, cur)}</div>
                            <div className="text-[10px] uppercase font-mono text-zinc-500">
                              {cadenceLabel(it.recurring)}
                            </div>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                );
              })}

              {/* Reference / sample links — rendered at the very bottom
                  of the section as a prominent call-to-action so the
                  client sees the items first, then is invited to click
                  through to references. Opens in a new tab. */}
              {(sec.sample_links || []).filter((l) => (l.url || "").trim()).length > 0 && (
                <div className="mt-5 space-y-2"
                  data-testid={`proposal-share-section-${sec.key}-links`}>
                  {(sec.sample_links || []).map((l, li) => {
                    if (!(l.url || "").trim()) return null;
                    const label = (l.label || l.url).trim();
                    return (
                      <a key={`${l.url || "samplelink"}_${li}`} href={l.url} target="_blank" rel="noopener noreferrer"
                        className="flex items-center justify-center gap-2 w-full px-4 py-3 rounded-sm border border-zinc-300 bg-white hover:border-zinc-900 hover:bg-zinc-50 text-sm font-semibold transition-colors"
                        data-testid={`proposal-share-section-${sec.key}-link-${li}`}>
                        <ExternalLink className="h-4 w-4" />
                        Click here to view {label}
                      </a>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}

        {/* Totals — big number = ex-GST, fine print = inc GST.
            First-year total sits on its own row below the 5 cadences so
            large currency amounts (e.g. $24,590) never get clipped. */}
        <div className="p-10 bg-zinc-900 text-white"
          data-testid="proposal-share-totals">
          <h2 className="font-display font-bold text-2xl mb-6">Investment summary</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-6">
            <TotalCol label="One-off" v={totals.one_off_subtotal} sub={totals.one_off_total} cur={cur} />
            <TotalCol label="Monthly" v={totals.monthly_subtotal} sub={totals.monthly_total} cur={cur} />
            <TotalCol label="Quarterly" v={totals.quarterly_subtotal} sub={totals.quarterly_total} cur={cur} />
            <TotalCol label="Annual" v={totals.annual_subtotal} sub={totals.annual_total} cur={cur} />
            <TotalCol label="Biennial" v={totals.biennial_subtotal} sub={totals.biennial_total} cur={cur} />
          </div>
          <div className="mt-6 pt-6 border-t border-zinc-700">
            <TotalCol label="First-year total" v={totals.year_one_subtotal} sub={totals.year_one_total} cur={cur} highlight />
          </div>
          <p className="text-[11px] text-zinc-400 mt-4">
            GST rate {((totals.gst_rate || 0) * 100).toFixed(0)}%. Biennial
            items are billed once every two years (e.g. domain renewals).
          </p>
        </div>

        {/* SEO seed inputs — competitor websites + priority search terms.
            Renders only if either list has entries so the share view stays
            tight on simple builds. */}
        {((doc.competitors || []).some((c) => (c.name || c.url || "").trim()) ||
          (doc.search_terms || []).some((t) => (t || "").trim())) && (
          <div className="p-10 space-y-8 border-t border-zinc-100"
            data-testid="proposal-share-seo-block">
            {(doc.competitors || []).some((c) => (c.name || c.url || "").trim()) && (
              <div data-testid="proposal-share-competitors">
                <h2 className="font-display font-bold text-2xl mb-2">Competitor or industry sample websites</h2>
                <p className="text-sm text-zinc-600 mb-4 max-w-3xl">
                  We&apos;ll seed these competitors into our rank-tracking and
                  backlink-monitoring tools so we can benchmark your
                  performance across the campaign.
                </p>
                <div className="grid sm:grid-cols-2 gap-2">
                  {(doc.competitors || [])
                    .filter((c) => (c.name || c.url || "").trim())
                    .map((c, i) => (
                      <div key={`${c.url || c.name || "comp"}_${i}`}
                        className="border border-zinc-200 rounded-sm p-3"
                        data-testid={`proposal-share-competitor-${i}`}>
                        {c.name && <div className="font-semibold text-sm">{c.name}</div>}
                        {c.url && (
                          <a href={c.url} target="_blank" rel="noopener noreferrer"
                            className="text-xs text-zinc-700 hover:underline break-all">
                            {c.url}
                          </a>
                        )}
                      </div>
                    ))}
                </div>
              </div>
            )}

            {(doc.search_terms || []).some((t) => (t || "").trim()) && (
              <div data-testid="proposal-share-search-terms">
                <h2 className="font-display font-bold text-2xl mb-2">
                  Priority search words &amp; phrases
                </h2>
                <p className="text-sm text-zinc-600 mb-4 max-w-3xl">
                  The search terms you&apos;d like us to prioritise when
                  optimising the site. We&apos;ll target them on relevant
                  landing pages and report on their ranking each month.
                </p>
                <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1">
                  {(doc.search_terms || [])
                    .map((t) => (t || "").trim())
                    .filter(Boolean)
                    .map((t, i) => (
                      <div key={`${t}_${i}`} className="text-sm text-zinc-800"
                        data-testid={`proposal-share-search-term-${i}`}>
                        <span className="text-zinc-400 mr-1">·</span> {t}
                      </div>
                    ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Footer sections */}
        <div className="p-10 space-y-6">
          {doc.inclusions_exclusions && <Section title="Inclusions & exclusions" body={doc.inclusions_exclusions} />}
          {doc.payment_terms && <Section title="Payment terms" body={doc.payment_terms} />}
          {doc.compliance_notes && <Section title="Compliance & approval requirements" body={doc.compliance_notes} />}
          {doc.expected_outcome && <Section title="Expected outcome" body={doc.expected_outcome} />}
          {/* Always shown last — the final thing the client reads before
              the sign-off block. Falls back to a placeholder so the
              heading is always visible to prompt a conversation. */}
          <Section
            title="Client Expectations and Deliverables"
            body={doc.client_expectations
              || "— To be confirmed during onboarding."}
          />
        </div>

        {/* Acceptance — locked summary if already accepted, else live
            form. Also renders in preview mode (disabled) so the admin can
            see exactly what the client will see at the bottom of the page. */}
        {doc.status === "accepted" ? (
          <AcceptedBlock doc={doc} />
        ) : (
          <SignatureBlock
            canvasRef={sigCanvasRef}
            signedName={signedName}
            setSignedName={setSignedName}
            signedTitle={signedTitle}
            setSignedTitle={setSignedTitle}
            sigDirty={sigDirty}
            setSigDirty={setSigDirty}
            onAccept={accept}
            busy={accepting}
            accentColor={doc.from_color_primary || "#002FA7"}
            disabled={signingDisabled}
          />
        )}

        <div className="p-6 border-t border-zinc-100 text-center text-[11px] text-zinc-400">
          Generated by {doc.prepared_by || "the agency"}
        </div>
      </div>
    </div>
  );
}

/**
 * Repeatable text inputs so the client can tell us about any domain(s)
 * they already own and would like to use for the project. Renders the
 * autosave status next to the heading. Locked once the proposal has
 * been accepted — at that point the values are part of the signed
 * audit trail.
 */
function ClientOwnedDomainsBlock({
  ownedDomains, status, locked, onUpdate, onAdd, onRemove, isAccepted,
  standalone = false,
}) {
  // When the proposal is locked (accepted), show only the values the
  // client signed off on so the read-only block stays unambiguous.
  if (locked || isAccepted) {
    const final = (ownedDomains || []).map((d) => (d || "").trim()).filter(Boolean);
    if (final.length === 0) return null;
    return (
      <div className={standalone ? "" : "mt-6 pt-5 border-t border-zinc-100"}
        data-testid="proposal-share-client-domains-locked">
        <div className="label-overline text-[10px] mb-2">Domain(s) you already own</div>
        <ul className="space-y-1">
          {final.map((d, i) => (
            <li key={`${d}_${i}`} className="font-mono text-sm text-zinc-800"
              data-testid={`proposal-share-client-domain-locked-${i}`}>
              {d}
            </li>
          ))}
        </ul>
      </div>
    );
  }
  return (
    <div className={standalone ? "" : "mt-6 pt-5 border-t border-zinc-100"}
      data-testid="proposal-share-client-domains-block">
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <div className="label-overline text-[10px]">
          {standalone
            ? "Already have a domain you would like to use?"
            : "Or — already have a domain you would like to use?"}
        </div>
        {status === "saving" && (
          <span className="text-[10px] text-zinc-400 font-mono"
            data-testid="proposal-share-client-domains-saving">saving…</span>
        )}
        {status === "saved" && (
          <span className="text-[10px] text-emerald-600 font-mono"
            data-testid="proposal-share-client-domains-saved">saved</span>
        )}
        {status === "error" && (
          <span className="text-[10px] text-red-600 font-mono"
            data-testid="proposal-share-client-domains-error">retry — couldn\u2019t save</span>
        )}
      </div>
      <p className="text-[11px] text-zinc-500 mb-3 max-w-2xl">
        Type any domain name(s) you already own and want to use for this
        project (e.g. <span className="font-mono">yourbusiness.com.au</span>).
        We&apos;ll point them at the new website once it&apos;s ready.
      </p>
      <div className="space-y-2">
        {(ownedDomains || [""]).map((d, i) => (
          <div key={`owned-domain-row-${i}`} className="flex items-center gap-2"
            data-testid={`proposal-share-client-domain-row-${i}`}>
            <input
              type="text"
              value={d || ""}
              onChange={(e) => onUpdate(i, e.target.value)}
              placeholder="yourbusiness.com.au"
              className="flex-1 h-10 rounded-sm border border-zinc-300 px-3 font-mono text-sm focus:outline-none focus:border-zinc-900"
              data-testid={`proposal-share-client-domain-input-${i}`}
            />
            {(ownedDomains || []).length > 1 && (
              <button
                type="button"
                onClick={() => onRemove(i)}
                className="h-10 w-10 grid place-items-center rounded-sm border border-zinc-200 text-zinc-500 hover:border-red-300 hover:text-red-600"
                title="Remove this row"
                data-testid={`proposal-share-client-domain-remove-${i}`}>
                ×
              </button>
            )}
          </div>
        ))}
      </div>
      {(ownedDomains || []).length < 20 && (
        <button
          type="button"
          onClick={onAdd}
          className="mt-2 text-xs underline text-zinc-600 hover:text-zinc-900"
          data-testid="proposal-share-client-domain-add">
          + Add another domain
        </button>
      )}
    </div>
  );
}



function Section({ title, body }) {
  return (
    <div>
      <h2 className="font-display font-bold text-2xl mb-2">{title}</h2>
      {body.split("\n").map((line, i) =>
        line.trim() ? <p key={`${line.slice(0, 32)}-${i}`} className="text-sm text-zinc-700 leading-relaxed mb-2">{line}</p> : null,
      )}
    </div>
  );
}

function TotalCol({ label, v, sub, cur, highlight }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-zinc-400">{label}</div>
      <div className={`font-display font-extrabold ${highlight ? "text-3xl" : "text-2xl"} mt-2`}>
        {fmtMoney(v, cur)}
      </div>
      <div className="text-[10px] font-mono text-zinc-500 mt-1">
        {typeof sub === "number" ? `inc GST ${fmtMoney(sub, cur)}` : sub}
      </div>
    </div>
  );
}

// ── Signature block (drawn canvas + typed name + position + accept) ────
function SignatureBlock({
  canvasRef, signedName, setSignedName, signedTitle, setSignedTitle,
  sigDirty, setSigDirty, onAccept, busy, accentColor, disabled,
}) {
  return (
    <div className="p-10 border-t-2 border-zinc-200 bg-zinc-50/60"
      data-testid="proposal-share-signature-block">
      <h2 className="font-display font-bold text-2xl mb-3 flex items-center gap-2">
        <PenLine className="h-5 w-5" />
        Sign &amp; accept this proposal
      </h2>
      {disabled && (
        <div className="mb-4 inline-flex items-center text-[11px] font-mono uppercase tracking-wide bg-amber-100 text-amber-900 px-2 py-0.5 rounded-sm"
          data-testid="proposal-share-signature-preview-banner">
          preview — disabled for admin
        </div>
      )}
      <p className="text-sm text-zinc-600 max-w-2xl mb-2">
        By signing below you accept the line items you&apos;ve selected
        above. The investment summary will lock to the totals shown when
        you sign. This signature constitutes your acceptance of the
        proposal.
      </p>
      <p className="text-[11px] text-zinc-500 max-w-2xl mb-5 italic">
        I confirm I am an authorised representative of the company named
        above and have the authority to bind that company to the terms
        of this proposal.
      </p>

      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <label className="text-[10px] uppercase tracking-widest text-zinc-500">
              Your full legal name
            </label>
            <input type="text" value={signedName}
              disabled={disabled}
              onChange={(e) => setSignedName(e.target.value)}
              placeholder="Jane Doe"
              className="w-full border border-zinc-300 rounded-sm px-3 h-10 text-sm disabled:bg-zinc-100 disabled:cursor-not-allowed"
              data-testid="proposal-share-signed-name" />
          </div>
          <div className="space-y-1.5">
            <label className="text-[10px] uppercase tracking-widest text-zinc-500">
              Position / title in the company
            </label>
            <input type="text" value={signedTitle}
              disabled={disabled}
              onChange={(e) => setSignedTitle(e.target.value)}
              placeholder="Managing Director, CFO, Owner…"
              className="w-full border border-zinc-300 rounded-sm px-3 h-10 text-sm disabled:bg-zinc-100 disabled:cursor-not-allowed"
              data-testid="proposal-share-signed-title" />
          </div>
        </div>

        <div className="space-y-1.5">
          <label className="text-[10px] uppercase tracking-widest text-zinc-500 flex items-center justify-between">
            <span>Draw your signature</span>
            <button type="button"
              disabled={disabled}
              onClick={() => {
                const c = canvasRef.current;
                if (c) {
                  const ctx = c.getContext("2d");
                  ctx.clearRect(0, 0, c.width, c.height);
                  setSigDirty(false);
                }
              }}
              className="inline-flex items-center gap-1 text-[10px] text-zinc-500 hover:text-zinc-900 disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="proposal-share-signature-clear">
              <Eraser className="h-3 w-3" /> Clear
            </button>
          </label>
          <SignaturePad ref={canvasRef}
            onDrawStart={() => setSigDirty(true)}
            disabled={disabled} />
        </div>

        <div className="text-[11px] text-zinc-500">
          Signing as <strong>{signedName || "—"}</strong>
          {signedTitle && <>, <strong>{signedTitle}</strong></>}
          {" on "}
          <strong>{new Date().toLocaleDateString("en-AU",
            { day: "2-digit", month: "long", year: "numeric" })}</strong>.
          The proposal will be locked to this state.
        </div>

        <button type="button"
          onClick={onAccept}
          disabled={busy || disabled || !signedName.trim() || !signedTitle.trim() || !sigDirty}
          className="inline-flex items-center gap-2 px-5 py-3 rounded-sm text-white text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
          style={{ background: accentColor }}
          data-testid="proposal-share-accept-button">
          <ShieldCheck className="h-4 w-4" />
          {busy ? "Saving…" : "Accept & sign proposal"}
        </button>
      </div>
    </div>
  );
}

// ── Locked acceptance summary (status === 'accepted') ──────────────────
function AcceptedBlock({ doc }) {
  return (
    <div className="p-10 border-t-2 border-emerald-200 bg-emerald-50/60"
      data-testid="proposal-share-accepted-block">
      <h2 className="font-display font-bold text-2xl mb-3 flex items-center gap-2 text-emerald-800">
        <ShieldCheck className="h-5 w-5" /> Proposal accepted
      </h2>
      <p className="text-sm text-emerald-800/80 max-w-2xl mb-5">
        Signed by <strong>{doc.accepted_signed_name}</strong>
        {doc.accepted_signed_title && <>, <strong>{doc.accepted_signed_title}</strong></>}
        {" on "}
        <strong>{doc.accepted_at
          ? new Date(doc.accepted_at).toLocaleString("en-AU")
          : "—"}</strong>. The selected line items and totals above are
        now locked to this record.
      </p>
      {doc.accepted_signature_png && (
        <div className="inline-block border border-emerald-200 bg-white p-3 rounded-sm">
          <img src={doc.accepted_signature_png} alt="Signature"
            className="max-h-24"
            data-testid="proposal-share-accepted-signature" />
        </div>
      )}
    </div>
  );
}

// ── Drawn signature canvas — small, dependency-free ────────────────────
const SignaturePad = React.forwardRef(function SignaturePad(
  { onDrawStart, disabled }, ref,
) {
  // Local ref kept in sync with the forwarded ref so the parent can read
  // the canvas (toDataURL / clear).
  const localRef = useRef(null);
  React.useImperativeHandle(ref, () => localRef.current, []);

  const drawing = useRef(false);
  const last = useRef(null);

  const getPos = (e) => {
    const c = localRef.current;
    const rect = c.getBoundingClientRect();
    const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    const y = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;
    return {
      x: x * (c.width / rect.width),
      y: y * (c.height / rect.height),
    };
  };

  const start = (e) => {
    if (disabled) return;
    e.preventDefault();
    drawing.current = true;
    last.current = getPos(e);
    if (onDrawStart) onDrawStart();
  };
  const move = (e) => {
    if (disabled || !drawing.current) return;
    e.preventDefault();
    const p = getPos(e);
    const ctx = localRef.current.getContext("2d");
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.strokeStyle = "#111";
    ctx.beginPath();
    ctx.moveTo(last.current.x, last.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    last.current = p;
  };
  const end = () => {
    drawing.current = false;
    last.current = null;
  };

  return (
    <canvas
      ref={localRef}
      width={760} height={180}
      style={{ touchAction: "none" }}
      className={`block w-full bg-white border border-zinc-300 rounded-sm ${
        disabled ? "cursor-not-allowed opacity-60" : "cursor-crosshair"
      }`}
      onMouseDown={start}
      onMouseMove={move}
      onMouseUp={end}
      onMouseLeave={end}
      onTouchStart={start}
      onTouchMove={move}
      onTouchEnd={end}
      data-testid="proposal-share-signature-canvas"
    />
  );
});
